import React, { useEffect, useState } from 'react';
import { useField } from 'formik';
import { FormElement, Label } from '@dashboard/components/Form';
import { Select } from '@dashboard/components/Formik';
import FormError from '@dashboard/components/Formik/components/FormError';
import { elementFieldTypeEnums } from '@dashboard/services/pageFieldTypes';

// Fields that can be used as triggers for conditional logic
const SUPPORTED_TRIGGER_FIELDS = [
  elementFieldTypeEnums.SELECT,
  elementFieldTypeEnums.MULTIPLE_CHOICE,
  elementFieldTypeEnums.CHECKBOX,
];

function getShouldSelectOption(comparison) {
  return comparison === 'IS' || comparison === 'IS_NOT';
}

export default function ShowHideLogicField({ fieldKey, field, fields }) {
  const [logicField, , helpers] = useField('showHideLogic');
  const { setValue } = helpers;

  const currentFieldId = field._id || field.key;

  const [selectedFieldId, setSelectedFieldId] = useState(
    logicField.value?.element?.elementId || null,
  );
  const [selectedValue, setSelectedValue] = useState(logicField.value?.value || null);

  const availableFields = fields
    .filter(f => f._id !== currentFieldId && SUPPORTED_TRIGGER_FIELDS.includes(f.element))
    .map(f => ({ ...f, id: f._id || f.key }));

  const selectedField = availableFields.find(f => f._id === selectedFieldId);
  const isCheckboxField = selectedField?.element === elementFieldTypeEnums.CHECKBOX;
  const isSelectOrMultiField =
    selectedField?.element === elementFieldTypeEnums.SELECT ||
    selectedField?.element === elementFieldTypeEnums.MULTIPLE_CHOICE;

  useEffect(() => {
    if (selectedFieldId) {
      const targetField = availableFields.find(f => f._id === selectedFieldId);

      if (!targetField) {
        return;
      }

      setValue({
        ...logicField.value,
        element: {
          elementId: targetField._id,
          elementType: 'field',
          elementTitle: targetField.label,
        },
      });
    }
  }, [selectedFieldId]);

  useEffect(() => {
    if (selectedValue !== null) {
      setValue({
        ...logicField.value,
        value: selectedValue,
      });
    }
  }, [selectedValue]);

  return (
    <div className="ml-[26px] !space-y-2 !mb-2 !mt-2">
      <FormElement>
        <div className="flex items-baseline space-x-2">
          <Label htmlFor={`${fieldKey}.showHideLogic.element.elementId`} className="w-16">
            Field
          </Label>

          <Select
            id={`${fieldKey}.showHideLogic.element.elementId`}
            name="showHideLogic.element.elementId"
            onChange={e => {
              setSelectedFieldId(e.target.value || null);
            }}
            value={selectedFieldId || ''}
          >
            <option value="" default>
              Select field...
            </option>
            {availableFields.map(f => (
              <option key={f._id} value={f._id}>
                {f.label}
              </option>
            ))}
          </Select>
        </div>
        <FormError name="showHideLogic.element.elementId" />
        <FormError name="showHideLogic.element.elementType" />
        <FormError name="showHideLogic.element.elementTitle" />
      </FormElement>
      <>
        <FormElement>
          <div className="flex items-baseline gap-x-2">
            <div className="w-16" />

            <Select
              id={`${fieldKey}.showHideLogic.comparison`}
              name="showHideLogic.comparison"
              className="w-32"
            >
              <option value="" default>
                Select condition...
              </option>
              <option value="IS">is</option>
              <option value="IS_NOT">is not</option>
              {!isCheckboxField && <option value="IS_EMPTY">is empty</option>}
              {!isCheckboxField && <option value="IS_NOT_EMPTY">is not empty</option>}
            </Select>
          </div>

          <FormError name="showHideLogic.comparison" />
        </FormElement>

        {selectedField && getShouldSelectOption(logicField.value?.comparison) && (
          <FormElement>
            <div className="flex items-baseline gap-x-2">
              {isCheckboxField ? (
                <>
                  <Label htmlFor={`${fieldKey}.showHideLogic.value`} className="w-16">
                    Value
                  </Label>

                  <Select
                    id={`${fieldKey}.showHideLogic.value`}
                    name="showHideLogic.value"
                    className="w-32"
                    onChange={e => {
                      setSelectedValue(e.target.value || null);
                    }}
                    value={selectedValue || ''}
                  >
                    <option value="" default>
                      Select value...
                    </option>
                    <option value="TRUE">Checked</option>
                    <option value="FALSE">Unchecked</option>
                  </Select>
                </>
              ) : isSelectOrMultiField ? (
                <>
                  <Label htmlFor={`${fieldKey}.showHideLogic.value`} className="w-16">
                    Value
                  </Label>

                  <Select
                    id={`${fieldKey}.showHideLogic.value`}
                    name="showHideLogic.value"
                    className="w-32"
                    onChange={e => {
                      setSelectedValue(e.target.value || null);
                    }}
                    value={selectedValue || ''}
                  >
                    <option value="" default>
                      Select option...
                    </option>
                    {selectedField.options?.map(option => (
                      <option key={option.key} value={option._id}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </>
              ) : null}
            </div>

            <FormError name="showHideLogic.value" />
          </FormElement>
        )}
      </>
    </div>
  );
}
